import Css from "./style.module.scss";

import coloredLogo from "assets/coloredLogo.svg";

import * as Icons from "@phosphor-icons/react";
import { Avatar, Pagination, PreloaderDotted, SelectPageSize } from "nlib/ui";
import { getActiveOrganization, getProjectName } from "selectors/organizations";
import { getCurrentQuickBooksRealmId, getCurrentXeroOrganizationId } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useMainApi } from "hooks";
import { useSelector } from "react-redux";
import ActivityEvents from "const/ActivityEvents";
import Colors from "const/Colors";
import Constants from "const/Constants";
import Countries from "const/Countries";
import DateTimeFormats from "const/DateTimeFormats";
import ExpandedListItem from "./lib/ExpandedListItem";
import Platforms from "utils/Platforms";
import React, { useCallback, useMemo, useState } from "react";
import Texts from "utils/Texts";
import UiRoutes from "const/UiRoutes";
import classNames from "classnames";
import moment from "moment";
import useActivityEventDescription from "hooks/useActivityEventDescription";

const {
  TYPES: {
    BUSINESS_SETTINGS_UPDATED,
    DOCUMENT_RECOGNITION_FAILED,
    DOCUMENTS_APPROVED,
    DOCUMENTS_AUTO_APPROVED,
    DOCUMENTS_DELETED,
    DOCUMENTS_UPLOADED,
    GPT_AI_FINE_TUNE_FAILED,
    GPT_AI_FINE_TUNE_FINISHED,
    GPT_AI_FINE_TUNE_STARTED,
    NEW_BUSINESS_ADDED,
    RPA_FAILED,
    RPA_FINISHED,
    RPA_STARTED,
    TASK_ADDED,
    TASK_MARKED_AS_COMPLETED,
    TASK_REMOVED,
    TASK_STATUS_CHANGED,
    TASK_TARGET_USER_CHANGED,
    TRANSACTIONS_APPROVED,
    TRANSACTIONS_ASK_CLIENT_FOLLOW_UP,
    TRANSACTIONS_ASK_CLIENT,
    TRANSACTIONS_AUTO_APPROVED,
    TRANSACTIONS_AUTO_ASK_CLIENT,
    TRANSACTIONS_AUTO_CATEGORIZED,
    TRANSACTIONS_AUTO_PAIRED,
    TRANSACTIONS_AUTO_PAIRED_AND_APPROVED,
    TRANSACTIONS_CATEGORIZED,
    TRANSACTIONS_EDITED,
    TRANSACTIONS_NEED_DOCUMENT,
    TRANSACTIONS_NOT_CATEGORIZED,
    TRANSACTIONS_REPLIED_BY_CLIENT,
    USER_INVITE_FOLLOW_UP,
    USER_INVITED,
    USER_REVOKED
  },
  EDITABLE_ACTIVITY_PROPERTIES
} = ActivityEvents;

const ICONS_BY_TYPE = {
  [BUSINESS_SETTINGS_UPDATED]: Icons.Gear,
  [DOCUMENT_RECOGNITION_FAILED]: Icons.CloudSlash,
  [DOCUMENTS_APPROVED]: Icons.Check,
  [DOCUMENTS_AUTO_APPROVED]: Icons.Check,
  [DOCUMENTS_DELETED]: Icons.Trash,
  [DOCUMENTS_UPLOADED]: Icons.CloudArrowUp,
  [GPT_AI_FINE_TUNE_FAILED]: Icons.Cpu,
  [GPT_AI_FINE_TUNE_FINISHED]: Icons.Cpu,
  [GPT_AI_FINE_TUNE_STARTED]: Icons.Cpu,
  [NEW_BUSINESS_ADDED]: Icons.Briefcase,
  [RPA_FAILED]: Icons.Cpu,
  [RPA_FINISHED]: Icons.Cpu,
  [RPA_STARTED]: Icons.Cpu,
  [TASK_ADDED]: Icons.CheckSquareOffset,
  [TASK_MARKED_AS_COMPLETED]: Icons.CheckSquare,
  [TASK_REMOVED]: Icons.Trash,
  [TASK_STATUS_CHANGED]: Icons.CheckSquare,
  [TASK_TARGET_USER_CHANGED]: Icons.User,
  [TRANSACTIONS_APPROVED]: Icons.Check,
  [TRANSACTIONS_ASK_CLIENT_FOLLOW_UP]: Icons.Envelope,
  [TRANSACTIONS_ASK_CLIENT]: Icons.Question,
  [TRANSACTIONS_AUTO_APPROVED]: Icons.Cpu,
  [TRANSACTIONS_AUTO_ASK_CLIENT]: Icons.Cpu,
  [TRANSACTIONS_AUTO_CATEGORIZED]: Icons.Cpu,
  [TRANSACTIONS_AUTO_PAIRED]: Icons.Cpu,
  [TRANSACTIONS_AUTO_PAIRED_AND_APPROVED]: Icons.Cpu,
  [TRANSACTIONS_CATEGORIZED]: Icons.Tag,
  [TRANSACTIONS_EDITED]: Icons.PencilSimple,
  [TRANSACTIONS_NEED_DOCUMENT]: Icons.FileSearch,
  [TRANSACTIONS_NOT_CATEGORIZED]: Icons.Cpu,
  [TRANSACTIONS_REPLIED_BY_CLIENT]: Icons.ChatText,
  [USER_INVITE_FOLLOW_UP]: Icons.Envelope,
  [USER_INVITED]: Icons.UserPlus,
  [USER_REVOKED]: Icons.UserMinus
};

const TYPE_COLOR = {
  [RPA_FAILED]: Css.failed,
  [GPT_AI_FINE_TUNE_FAILED]: Css.failed
};

const PREV_VALUE_PROP_NAMES = Object.fromEntries(
  EDITABLE_ACTIVITY_PROPERTIES.map(({ propName, prevPropName }) => [propName, prevPropName])
);

const PAGINATION_MAX_DISPLAYED_PAGES = 7;

const ActivityItem = ({ item }) => {
  const {
    id: activityId,
    type,
    system,
    info,
    timestamp,
    expandedList = [],
    expandedListSize
  } = item;

  const { uiTexts } = useSelector(getTextsData);

  const activeOrganization = useSelector(getActiveOrganization);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const projectName = useSelector(getProjectName);

  const [expandedListShown, setExpandedListShown] = useState(false);

  const [page, setPage] = useState(1);

  const [perPage, setPerPage] = useState(Constants.TABLE_PAGE_SIZE);

  const [{ expandedList: fullExpandedList }, fetching] = useMainApi({
    initialData: { expandedList: [] },
    fetchCondition: expandedListShown,
    parameters: [`${UiRoutes.ACTIVITY}/${activityId}`],
    dependencies: [expandedListShown]
  });

  const itemsList = (fullExpandedList.length ? fullExpandedList : expandedList);

  const columnNames = useMemo(() => {
    const usCountry = activeOrganization.countryCode === Countries.US;

    const hasReason = type === TRANSACTIONS_REPLIED_BY_CLIENT
      && itemsList.some((transaction) => !transaction.category && !transaction.prevCategory
      && !transaction.item && !transaction.prevItem && (transaction.reason || transaction.prevReason));

    const hasItem = itemsList.some((transaction) => transaction.item || transaction.prevItem);

    return [
      ["payee"],
      ![TRANSACTIONS_AUTO_PAIRED_AND_APPROVED, TRANSACTIONS_AUTO_PAIRED].includes(type) && [
        hasReason && "reason",
        ((!hasReason && !hasItem) || itemsList.some((transaction) => transaction.category || transaction.prevCategory))
          && "category",
        itemsList.some((transaction) => transaction.item || transaction.prevItem) && "item"
      ].filter(Boolean),
      ["class"],
      quickBooksBusiness && ["location"],
      !xeroBusiness && ["project"],
      !usCountry && ["taxRate"]
    ].filter(Boolean);
  }, [
    type,
    itemsList,
    xeroBusiness,
    activeOrganization,
    quickBooksBusiness
  ]);

  const shownColumnNames = useMemo(() => {
    return columnNames.filter((keys) => {
      return itemsList.some((transaction) => keys.some((key) => transaction[key] || transaction[PREV_VALUE_PROP_NAMES[key]]));
    });
  }, [columnNames, itemsList]);

  const descriptionColumnShown = useMemo(() => {
    return itemsList.some((transaction) => transaction.description);
  }, [itemsList]);

  const matchedInfoColumnShown = useMemo(() => {
    return itemsList.some((transaction) => transaction.matchedInfo?.type);
  }, [itemsList]);

  const matchedInfoLinksDisabled = useMemo(() => {
    return itemsList.some((transaction) => transaction.matchedInfo?.advanced);
  }, [itemsList]);

  const userName = useMemo(() => {
    if (system) return projectName;

    const { user, sender, recipient, businessName } = info;

    return (user && (user.userFullName || user.email))
      || (sender && (sender.userFullName || sender.email))
      || (recipient && (recipient.userFullName || recipient.email))
      || businessName
      || uiTexts.unknown;
  }, [info, projectName, system, uiTexts]);

  const description = useActivityEventDescription(item);

  const timeDifference = moment(timestamp).diff(moment());

  const { logoUrl = coloredLogo } = activeOrganization.whiteLabel || {};

  const Icon = ICONS_BY_TYPE[type] || Icons.Pulse;

  const partialResult = type === RPA_FAILED && info.partialResult;

  const handleShowMoreClick = useCallback(() => {
    setExpandedListShown(true);
  }, []);

  const handlePageChange = useCallback((value) => {
    setPage(value);
  }, []);

  const handlePageSizeChange = useCallback((value) => {
    setPage(1);
    setPerPage(value);
  }, []);

  return (
    <div className={classNames(Css.listItem, system && Css.system)}>
      <div className={classNames(Css.type, !partialResult && TYPE_COLOR[type])}><Icon /></div>
      <div className={Css.card}>
        <div className={Css.title}>
          <Avatar
            className={Css.avatar}
            backgroundColor={system ? Colors.WHITE : undefined}
            src={system ? logoUrl : undefined}
            title={userName} />
          <div className={Css.titleContent}>
            <div className={Css.label}>
              {description}
            </div>
            <div className={Css.date}>
              {Math.abs(timeDifference) > moment.duration(1, "days").asMilliseconds()
                ? moment(timestamp).format(DateTimeFormats.DATETIME_TEXT)
                : moment.duration(timeDifference).humanize(true)}
            </div>
          </div>
        </div>
        {!!itemsList?.length && (
          <div className={Css.list}>
            {type !== ActivityEvents.TYPES.DOCUMENT_RECOGNITION_FAILED && (
              <ExpandedListItem
                header
                type={type}
                columnNames={columnNames}
                possibleColumnCount={columnNames.length}
                shownColumnNames={shownColumnNames}
                matchedInfoColumnShown={matchedInfoColumnShown}
                matchedInfoLinksDisabled={matchedInfoLinksDisabled}
                descriptionColumnShown={descriptionColumnShown} />
            )}
            {itemsList
              .slice((page - 1) * perPage, page * perPage)
              .map((listItem, index) => (
                <ExpandedListItem
                  key={String(index + 1)}
                  type={type}
                  item={listItem}
                  possibleColumnCount={columnNames.length}
                  shownColumnNames={shownColumnNames}
                  descriptionColumnShown={descriptionColumnShown}
                  matchedInfoColumnShown={matchedInfoColumnShown}
                  matchedInfoLinksDisabled={matchedInfoLinksDisabled} />
              ))}
            {!!(expandedListSize > itemsList.length) && (
              <div className={Css.footer}>
                <div>
                  {expandedListShown && fetching
                    ? <PreloaderDotted className={Css.preloader} />
                    : (
                      <div disabled={expandedListShown} className={Css.more} onClick={handleShowMoreClick}>
                        {Texts.replaceVars(
                          uiTexts.andCountMore.toLowerCase(),
                          { count: expandedListSize - itemsList.length }
                        )}
                      </div>
                    )}
                </div>
              </div>
            )}
            {expandedListShown && !fetching && expandedListSize > Constants.TABLE_PAGE_SIZE && (
              <div className={Css.footer}>
                <Pagination
                  pageSize={perPage}
                  count={expandedListSize}
                  page={page}
                  maxDisplayedPages={Platforms.checkIsTouchDevice() ? 1 : PAGINATION_MAX_DISPLAYED_PAGES}
                  onChange={handlePageChange} />
                {!Platforms.checkIsTouchDevice() && <SelectPageSize pageSize={perPage} onChange={handlePageSizeChange} />}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ActivityItem);
